import {
  Href,
  Link,
  router,
  useFocusEffect,
  useLocalSearchParams,
  useNavigation,
} from "expo-router";
import { CirclePlusIcon } from "lucide-react-native";
import { useCallback, useMemo, useState } from "react";

import { PlayerMatchReportsSection } from "@/features/player-match-reports";

import MainView from "./MainView";
import MatchEvaluationRow from "@/features/reports/components/molecules/MatchEvaluationRow";
import PlayerReportsIdentityHeader from "@/features/reports/components/organisms/PlayerReportsIdentityHeader";
import ReportItem from "@/features/reports/components/organisms/ReportItem";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { ListRenderItem } from "@/components/atoms/FlashList";
import { useCurrentUser } from "@/features/auth";
import {
  PlayerReportRow,
  mergePlayerReportRows,
} from "@/features/reports/utils/mergePlayerReportRows";
import { ReportKind } from "@/generated/graphql";
import useExpandedPlayerUrl from "@/hooks/useExpandedPlayerUrl";
import { usePlayerDetailQuery } from "@/hooks/usePlayerDetailQuery";
import useSetPageTitle from "@/hooks/useSetPageTitle";
import { useCanAccessFeature } from "@/providers/FeatureFlags";

const LOG_PREFIX = "[ScoutReport:ReportsList]";

/**
 * Displays scout reports for a player with admin-only report creation capability.
 * Conditionally renders the add report button based on user role and feature flags.
 * Refetches fresh data from the server every time the screen gains focus to ensure
 * newly submitted reports appear immediately without waiting for the poll interval.
 * @module features/reports/screens/Main
 */
const MainContainer = () => {
  const currentUser = useCurrentUser();
  const isAdmin = currentUser?.isAdmin;
  const intakeFormEnabled = useCanAccessFeature("intakeEnabled");
  const navigation = useNavigation();
  const { playerId, reportId } = useLocalSearchParams<{
    playerId: string;
    reportId?: string;
  }>();
  const matchesUrl = useExpandedPlayerUrl(`/players/${playerId}/matches`);
  // `playerId` is momentarily undefined/"" while the route mounts and tears
  // down despite its `string` type, so the query must stay skipped until it
  // resolves — otherwise the poll fires with `id: ""` and the server rejects it
  // (`Variable "$id" got invalid value ""` — SE-5638). Trimmed to match the
  // hook backstop's definition of empty: a URL like `/players/%20/reports`
  // yields `id: " "`, which is truthy but just as invalid to the server.
  const { data, refetch } = usePlayerDetailQuery({
    variables: { id: playerId ?? "" },
    skip: !playerId?.trim(),
  });

  const player = data?.getPlayer;

  const scoutReports = useMemo(
    () => player?.scoutReports ?? [],
    [player?.scoutReports]
  );

  // SE-7856: single-player scout reports and match-report fixtures in ONE
  // list, newest first by the date each row shows. Grouped per fixture, so two
  // scouts on one match are one row with the second read inside it — not two
  // rows claiming the player was watched twice.
  const rows = useMemo(
    () =>
      mergePlayerReportRows(
        player?.scoutReports,
        player?.matchReportEvaluations
      ),
    [player?.scoutReports, player?.matchReportEvaluations]
  );

  // Expansion lives here rather than in the row: the list is what knows which
  // fixture is open, and a molecule may not compose another molecule. Keyed by
  // `matchId`, which is the match row's identity.
  const [expandedMatchIds, setExpandedMatchIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  const handleToggleMatchRow = useCallback((matchId: string) => {
    setExpandedMatchIds(current =>
      current.has(matchId)
        ? new Set([...current].filter(id => id !== matchId))
        : new Set([...current, matchId])
    );
  }, []);

  const handleOpenMatchReport = useCallback((reportId: string) => {
    router.push(`/match-reports/${reportId}` as Href);
  }, []);

  // Match the player page header (backend-canonical player.name, SE-5469) so the
  // downloaded PDF title and filename match what the user sees on screen,
  // including compound surnames like "Virgil van Dijk" (SE-4629).
  const playerName = useMemo(
    () => (player ? player.name : undefined),
    [player]
  );

  const renderItem: ListRenderItem<PlayerReportRow> = useCallback(
    ({ item }) => {
      if (item.kind === ReportKind.Match) {
        return (
          <MatchEvaluationRow
            row={item}
            isExpanded={expandedMatchIds.has(item.matchId)}
            onToggleRow={handleToggleMatchRow}
            onOpenReport={handleOpenMatchReport}
          />
        );
      }

      return (
        <ReportItem
          item={item.report}
          playerName={playerName}
          // The deep link only ever names a scout report, and `defaultOpen` is
          // initial state that never re-syncs — so it stays on this branch.
          defaultOpen={item.report.reportId === reportId}
        />
      );
    },
    [
      reportId,
      playerName,
      expandedMatchIds,
      handleToggleMatchRow,
      handleOpenMatchReport,
    ]
  );

  // SE-6986: every match report this player appears in, above the existing
  // single-player scout reports. Memoised because the header re-mounting on
  // every list render would collapse the user's expanded rows.
  //
  // SE-7237: the identity header leads it. This route mounts the reports
  // feature on its own — there is no player-page shell above it — so without
  // this the screen presented aggregate scores about a subject it never named,
  // and offered no route back to the player. `player` is already in hand from
  // the query above; no extra fetch pays for it.
  const listHeader = useMemo(
    () => (
      <>
        <PlayerReportsIdentityHeader player={player} playerId={playerId} />
        <PlayerMatchReportsSection
          playerId={playerId}
          hasScoutReports={scoutReports.length > 0}
          playerName={playerName}
        />
      </>
    ),
    [player, playerId, scoutReports.length, playerName]
  );

  // Every row carries its own identity from the merge: `matchId` for a
  // fixture, `reportId` (or an index fallback) for a scout report.
  const keyExtractor = useCallback((item: PlayerReportRow) => item.rowId, []);

  useFocusEffect(
    useCallback(() => {
      if (isAdmin || intakeFormEnabled) {
        navigation.setOptions({
          headerRight: () => (
            <Box className="pr-3">
              <Link asChild href={matchesUrl as Href}>
                <Button
                  variant="link"
                  icon={CirclePlusIcon}
                  accessibilityLabel="Add scout report"
                />
              </Link>
            </Box>
          ),
        });
      }

      // `refetch` bypasses the query's `skip` — and the hook backstop only
      // gates the query, never an imperative refetch — so this needs the same
      // trimmed id guard, or the focus effect still issues the rejected
      // empty/whitespace-id request the skip above exists to prevent (SE-5638).
      if (!playerId?.trim()) {
        return;
      }

      console.info(LOG_PREFIX, "Screen focused — refetching scout reports", {
        playerId,
        currentReportCount: scoutReports.length,
      });
      refetch()
        .then(result => {
          const freshCount = result.data?.getPlayer?.scoutReports?.length ?? 0;
          console.info(LOG_PREFIX, "Refetch complete", {
            previousCount: scoutReports.length,
            freshCount,
            newReportsDetected: freshCount > scoutReports.length,
          });
        })
        .catch(err => {
          console.warn(LOG_PREFIX, "Refetch failed:", err?.message);
        });
    }, [
      intakeFormEnabled,
      isAdmin,
      navigation,
      matchesUrl,
      refetch,
      playerId,
      scoutReports.length,
    ])
  );

  useSetPageTitle("Reports");

  return (
    <MainView
      listHeader={listHeader}
      items={rows as PlayerReportRow[]}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
    />
  );
};

export default MainContainer;
